import { Component, EventEmitter, Output, OnDestroy } from '@angular/core'
import { ClusteringNormalization, PipelineType, UmapParameters } from '../../../shared/model/jobs'
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms'
import { combineLatest, Subject, take, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import {
    selectCaseMarkers,
    selectLoadedSegObjectPropertyMaps,
    selectMatchedUmapResults,
} from '../../../state/case/case.selectors'
import { SegObjectPropertyMap } from '../../../state/case/case.state'
import { selectJobs } from '../../../state/job/job.selectors'
import { environment } from 'src/environments/environment'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { MarkerCatalogService } from '../../../shared/services/marker-catalog.service'
import { selectImageLayers, selectAnnotationLayers } from 'src/app/state/viewer/viewer.selectors'

@Component({
    selector: 'app-umap-job',
    templateUrl: './umap-job.component.html',
    styleUrls: ['./umap-job.component.scss'],
})
export class UmapJobComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)

    umapForm: FormGroup

    formIsVisible = false
    createJobMode = true
    readonly clusteringMarkers$ = this.store.select(selectCaseMarkers)
    readonly matchedUmapResults$ = this.store.select(selectMatchedUmapResults)
    readonly imageLayers$ = this.store.select(selectImageLayers)
    readonly annotationLayers$ = this.store.select(selectAnnotationLayers)
    readonly SELECTED_EXPERIMENT_NAME_KEY = 'selectedExperimentName'
    readonly NEW_EXPERIMENT_NAME_KEY = 'newExperimentName'
    readonly LAYER_TYPE_MARKERS_KEY = 'markers'
    readonly LAYER_TYPE_TRANSCRIPTS_KEY = 'transcripts'
    readonly LAYER_TYPE_MORPHOMETRICS_KEY = 'morphometrics'
    readonly NUMBER_OF_NEIGHBORS_KEY = 'numberOfNeighbors'
    readonly MINIMUM_DISTANCE_KEY = 'minimumDistance'
    readonly SPREAD_KEY = 'spread'
    readonly NORMALIZATION_FORM_KEY = 'normalization'

    experiments = [{ name: 'Create new', x: 'umap1_X', y: 'umap1_Y' }]

    layerTypes: { name: string; formControl: string; disabled: boolean }[] = []

    private readonly destroy$ = new Subject<void>()
    jobIsRunning = false
    private readonly wikiUrl = environment.wikiUrl
    readonly loadedSegObjectPropertyMaps$ = this.store.select(selectLoadedSegObjectPropertyMaps)
    private loadedIntensityMaps: SegObjectPropertyMap[] = []

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService,
        private readonly markerCatalog: MarkerCatalogService
    ) {
        this.buildForm(this.experiments[0])

        this.store.select(selectJobs).subscribe(jobs => {
            const job = jobs.find(
                j => j.type === 'UMA' && j.status !== 'Rejected' && j.status !== 'Closed'
            )
            this.jobIsRunning = job ? true : false
        })

        combineLatest([this.loadedSegObjectPropertyMaps$, this.matchedUmapResults$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([maps, umap]) => {
                this.loadedIntensityMaps = maps
                this.experiments = [
                    { name: 'Create new', x: 'umap1_X', y: 'umap1_Y' },
                    ...umap.map(result => ({
                        name: result.name,
                        x: result.x,
                        y: result.y,
                        numberOfNeighbors: result.number_of_neighbors,
                        minimumDistance: result.minimum_distance,
                        spread: result.spread,
                        normalization: result.normalization,
                        markers: result.markers,
                        transcripts: result.transcripts,
                        morphometrics: result.morphometrics,
                    })),
                ]
                this.ensureValidExperimentSelection()
            })

        combineLatest([this.imageLayers$, this.annotationLayers$])
            .pipe(take(1))
            .subscribe(([imageLayers, annotationLayers]) => {
                this.layerTypes = [
                    {
                        name: 'Marker',
                        formControl: this.LAYER_TYPE_MARKERS_KEY,
                        disabled: imageLayers.length === 0,
                    },
                    {
                        name: 'Transcripts',
                        formControl: this.LAYER_TYPE_TRANSCRIPTS_KEY,
                        disabled: annotationLayers.filter(l => l.name !== 'selection').length === 0,
                    },
                    {
                        name: 'Morphometrics',
                        formControl: this.LAYER_TYPE_MORPHOMETRICS_KEY,
                        disabled: false,
                    },
                ]
            })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<UmapParameters>()

    get paramsValid(): boolean {
        if (
            !this.umapForm.value.markers &&
            !this.umapForm.value.transcripts &&
            !this.umapForm.value.morphometrics
        ) {
            return false
        }
        return this.umapForm.valid
    }

    /** Field validation lines (also used to build the Start Job tooltip) */
    get validationSummaryMessages(): string[] {
        const msgs: string[] = []
        if (!this.umapForm) return msgs

        const selectedCtl = this.umapForm.get(this.SELECTED_EXPERIMENT_NAME_KEY)
        if (selectedCtl?.hasError('required')) {
            msgs.push('Choose an experiment.')
        }

        if (this.createJobMode) {
            const newNameCtl = this.umapForm.get(this.NEW_EXPERIMENT_NAME_KEY)
            if (newNameCtl?.hasError('required')) {
                msgs.push('Enter an experiment name.')
            }
        }

        const neighborsCtl = this.umapForm.get(this.NUMBER_OF_NEIGHBORS_KEY)
        if (neighborsCtl?.hasError('required')) {
            msgs.push('Enter number of neighbors.')
        } else if (neighborsCtl?.hasError('min')) {
            msgs.push('Number of neighbors must be at least 2.')
        }

        const minDistCtl = this.umapForm.get(this.MINIMUM_DISTANCE_KEY)
        if (minDistCtl?.hasError('required')) {
            msgs.push('Enter minimum distance.')
        } else if (minDistCtl?.hasError('min')) {
            msgs.push('Minimum distance must be greater than 0.')
        }

        const spreadCtl = this.umapForm.get(this.SPREAD_KEY)
        if (spreadCtl?.hasError('required')) {
            msgs.push('Enter spread.')
        } else if (spreadCtl?.hasError('min')) {
            msgs.push('Spread must be greater than 0.')
        }

        if (
            this.umapForm.errors?.['minimumDistanceExceedsSpread'] &&
            (this.umapForm.get(this.MINIMUM_DISTANCE_KEY)?.dirty ||
                this.umapForm.get(this.SPREAD_KEY)?.dirty)
        ) {
            msgs.push('Minimum distance must be less than or equal to spread.')
        }

        return msgs
    }

    /** Shown as native tooltip when Start Job is disabled or job running */
    get startJobTooltip(): string | null {
        if (!this.formIsVisible || !this.umapForm) {
            return null
        }
        if (this.jobIsRunning) {
            return 'A UMAP job is already running.'
        }
        if (!this.createJobMode) {
            return 'Select "Create new" and enter an experiment name to start a job.'
        }
        if (this.paramsValid) {
            return null
        }
        const msgs: string[] = [...this.validationSummaryMessages]
        const v = this.umapForm.value
        if (!v.markers && !v.transcripts && !v.morphometrics) {
            msgs.push('Select at least one input feature.')
        }
        return msgs.length > 0 ? msgs.join('\n') : null
    }

    confirmParams(): void {
        if (!this.paramsValid || !this.createJobMode) return
        if (this.loadedIntensityMaps.length === 0) {
            this.toaster.httpError(
                new HttpErrorResponse({ error: 'Select an intensity map' }),
                ''
            )
            return
        }
        const {
            selectedExperimentName: _selected,
            newExperimentName,
            ...formFields
        } = this.umapForm.getRawValue()
        const inputFeature = ['markers', 'morphometrics', 'transcripts'].filter(
            feature => formFields[feature]
        )
        this.jobApi
            .startClusteringJob({
                type: PipelineType.umapClustering,
                ...formFields,
                newExperimentName,
                normalization: formFields.normalization || 'Per Feature',
                input_feature: inputFeature,
                seg_object_property_maps: this.loadedIntensityMaps,
            })
            .subscribe({
                next: () => closeJobSelectionDialog(),
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Error starting job')
                },
            })

        this.jobIsRunning = true
    }

    displayForm(): void {
        this.formIsVisible = !this.formIsVisible
        this.createJobMode = true
        if (this.formIsVisible) {
            this.buildForm(this.experiments[0])
        }
    }

    setExperiment(experimentName: string): void {
        if (experimentName === 'Create new') {
            this.createJobMode = true
            this.setNewExperimentNameRequired(true)
            return
        }
        const experiment = this.experiments.find(ex => ex.name === experimentName)
        if (!experiment) return
        this.createJobMode = false
        this.buildForm(experiment)
        this.setNewExperimentNameRequired(false)
        this.setDataAxis(experiment.x, experiment.y)
    }

    buildForm(experiment: any) {
        const createMode = !experiment?.name || experiment.name === 'Create new'
        this.createJobMode = createMode
        this.umapForm = this.formBuilder.group({
            [this.SELECTED_EXPERIMENT_NAME_KEY]: new FormControl(experiment?.name ?? 'Create new', [
                Validators.required,
            ]),
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl(
                '',
                createMode ? [Validators.required] : []
            ),
            [this.LAYER_TYPE_MARKERS_KEY]: new FormControl(experiment?.markers),
            [this.LAYER_TYPE_TRANSCRIPTS_KEY]: new FormControl(experiment?.transcripts),
            [this.LAYER_TYPE_MORPHOMETRICS_KEY]: new FormControl(experiment?.morphometrics),
            [this.NUMBER_OF_NEIGHBORS_KEY]: new FormControl(experiment?.numberOfNeighbors, [
                Validators.required,
                Validators.min(2),
            ]),
            [this.MINIMUM_DISTANCE_KEY]: new FormControl(experiment?.minimumDistance, [
                Validators.required,
                Validators.min(Number.EPSILON),
            ]),
            [this.SPREAD_KEY]: new FormControl(experiment?.spread, [
                Validators.required,
                Validators.min(Number.EPSILON),
            ]),
            [this.NORMALIZATION_FORM_KEY]: new FormControl(experiment?.normalization),
        }, { validators: this.minimumDistanceLessThanOrEqualSpreadValidator })
    }

    private ensureValidExperimentSelection(): void {
        if (!this.umapForm) return
        const selected = this.umapForm.get(this.SELECTED_EXPERIMENT_NAME_KEY)?.value
        if (this.experiments.some(experiment => experiment.name === selected)) return
        this.createJobMode = true
        this.umapForm.patchValue(
            { [this.SELECTED_EXPERIMENT_NAME_KEY]: 'Create new' },
            { emitEvent: false }
        )
        this.setNewExperimentNameRequired(true)
    }

    private setNewExperimentNameRequired(required: boolean): void {
        const control = this.umapForm?.get(this.NEW_EXPERIMENT_NAME_KEY)
        if (!control) return
        control.setValidators(required ? [Validators.required] : [])
        control.updateValueAndValidity({ emitEvent: false })
    }

    private readonly minimumDistanceLessThanOrEqualSpreadValidator = (
        control: AbstractControl
    ): ValidationErrors | null => {
        const minimumDistance = control.get(this.MINIMUM_DISTANCE_KEY)?.value
        const spread = control.get(this.SPREAD_KEY)?.value

        if (minimumDistance === null || minimumDistance === undefined || spread === null || spread === undefined) {
            return null
        }

        return Number(minimumDistance) <= Number(spread)
            ? null
            : { minimumDistanceExceedsSpread: true }
    }

    setDataAxis(markerXAxis: string, markerYAxis: string): void {
        const markerX = this.markerCatalog.getFullMarker(markerXAxis)
        const markerY = this.markerCatalog.getFullMarker(markerYAxis)
        if (!markerX || !markerY) {
            this.toaster.error(
                `Could not load plot axes "${markerXAxis}" and "${markerYAxis}".`,
                'Error'
            )
            return
        }

        this.store.dispatch(
            plotDataActions.loadDataBothAxes({
                markerXAxis: markerX,
                markerYAxis: markerY,
            })
        )
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Working_with_jobs#h-7-umap-and-t-sne`, '_blank')
    }
}
